import React, { useState, useEffect, useRef } from 'react';
import { API_BASE_URL } from '../../config.js';

const getFullAssetUrl = (url) => {
  if (!url) return '';
  if (url.startsWith('data:') || url.startsWith('http://') || url.startsWith('https://')) return url;
  const cleanUrl = url.startsWith('/') ? url : `/${url}`;
  return `${API_BASE_URL}${cleanUrl}`;
};

const TeacherPhotoModal = ({
  isOpen,
  onClose,
  teacher,
  token,
  onPhotoUpdated,
  onPermissionUpdated
}) => {
  const [canUpdatePhoto, setCanUpdatePhoto] = useState(false);
  const [photoUrl, setPhotoUrl] = useState('');
  const [isUpdatingPermission, setIsUpdatingPermission] = useState(false);
  const [isUploadingPhoto, setIsUploadingPhoto] = useState(false);
  const [notification, setNotification] = useState(null);
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (teacher) {
      setCanUpdatePhoto(Boolean(teacher.canUpdatePhoto));
      setPhotoUrl(teacher.photoUrl || teacher.photo || '');
      setNotification(null);
    }
  }, [teacher, isOpen]);

  if (!isOpen || !teacher) return null;

  const showMsg = (type, text) => {
    setNotification({ type, text });
    setTimeout(() => setNotification(null), 4000);
  };

  const handleTogglePermission = async () => {
    const nextVal = !canUpdatePhoto;
    setIsUpdatingPermission(true);
    try {
      const activeToken = token || localStorage.getItem('authToken') || localStorage.getItem('token') || '';
      const res = await fetch(`${API_BASE_URL}/api/teacher/${teacher.teacherId}/photo-permission`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${activeToken}`
        },
        body: JSON.stringify({ canUpdatePhoto: nextVal })
      });
      const data = await res.json();
      if (res.ok && data.success !== false) {
        setCanUpdatePhoto(nextVal);
        teacher.canUpdatePhoto = nextVal;
        showMsg('success', nextVal
          ? 'Mobile photo update permission GRANTED. Teacher can now update photo via app.'
          : 'Mobile photo update permission REVOKED. Photo update is now locked.');
        if (onPermissionUpdated) onPermissionUpdated(teacher.teacherId, nextVal);
      } else {
        showMsg('error', data.error || 'Failed to update photo permission');
      }
    } catch (err) {
      showMsg('error', 'Network error updating photo permission');
    } finally {
      setIsUpdatingPermission(false);
    }
  };

  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      showMsg('error', 'Please select a valid image file (JPEG, PNG, WEBP)');
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      showMsg('error', 'Image size should be less than 5 MB');
      return;
    }

    setIsUploadingPhoto(true);
    const reader = new FileReader();
    reader.onload = async () => {
      try {
        const base64Data = reader.result;
        const activeToken = token || localStorage.getItem('authToken') || localStorage.getItem('token') || '';
        const res = await fetch(`${API_BASE_URL}/api/teacher/${teacher.teacherId}/photo`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${activeToken}`
          },
          body: JSON.stringify({ photo: base64Data })
        });
        const data = await res.json();
        if (res.ok && data.success !== false) {
          const newUrl = data.photoUrl || base64Data;
          setPhotoUrl(newUrl);
          teacher.photoUrl = newUrl;
          teacher.photo = newUrl;
          showMsg('success', 'Profile photo updated successfully!');
          if (onPhotoUpdated) onPhotoUpdated(teacher.teacherId, newUrl);
        } else {
          showMsg('error', data.error || 'Failed to upload photo');
        }
      } catch (err) {
        showMsg('error', 'Network error uploading photo');
      } finally {
        setIsUploadingPhoto(false);
        if (fileInputRef.current) fileInputRef.current.value = '';
      }
    };
    reader.readAsDataURL(file);
  };

  const handleRemovePhoto = async () => {
    if (!window.confirm(`Are you sure you want to remove the profile photo for ${teacher.teacherName}?`)) {
      return;
    }
    setIsUploadingPhoto(true);
    try {
      const activeToken = token || localStorage.getItem('authToken') || localStorage.getItem('token') || '';
      const res = await fetch(`${API_BASE_URL}/api/teacher/${teacher.teacherId}/photo`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${activeToken}`
        },
        body: JSON.stringify({ photo: 'REMOVE' })
      });
      const data = await res.json();
      if (res.ok && data.success !== false) {
        setPhotoUrl('');
        teacher.photoUrl = null;
        teacher.photo = null;
        showMsg('success', 'Profile photo removed.');
        if (onPhotoUpdated) onPhotoUpdated(teacher.teacherId, null);
      } else {
        showMsg('error', data.error || 'Failed to remove photo');
      }
    } catch (err) {
      showMsg('error', 'Network error removing photo');
    } finally {
      setIsUploadingPhoto(false);
    }
  };

  const getInitials = (name) => {
    if (!name) return 'TC';
    const parts = name.trim().split(' ');
    if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
    return name.slice(0, 2).toUpperCase();
  };

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(15, 23, 42, 0.65)',
        backdropFilter: 'blur(4px)',
        zIndex: 9999,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '16px'
      }}
      onClick={onClose}
    >
      <div
        style={{
          backgroundColor: '#ffffff',
          borderRadius: '20px',
          boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
          maxWidth: '520px',
          width: '100%',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          border: '1px solid #e2e8f0',
          animation: 'fadeInScale 0.2s cubic-bezier(0.16, 1, 0.3, 1)'
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div
          style={{
            padding: '18px 24px',
            borderBottom: '1px solid #e2e8f0',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            background: 'linear-gradient(to right, #f8fafc, #ffffff)'
          }}
        >
          <div>
            <h3 style={{ margin: 0, fontSize: '18px', fontWeight: '700', color: '#0f172a' }}>
              Faculty Profile Photo & Permissions
            </h3>
            <span style={{ fontSize: '12px', color: '#64748b' }}>
              Manage teacher photo preview and mobile app update permissions
            </span>
          </div>
          <button
            type="button"
            onClick={onClose}
            style={{
              background: '#f1f5f9',
              border: 'none',
              borderRadius: '50%',
              width: '32px',
              height: '32px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              color: '#475569',
              fontSize: '16px',
              fontWeight: '700'
            }}
          >
            ✕
          </button>
        </div>

        {/* Body */}
        <div style={{ padding: '24px', display: 'flex', flexDirection: 'column', gap: '20px', alignItems: 'center' }}>
          {/* Notification Alert */}
          {notification && (
            <div
              style={{
                width: '100%',
                padding: '10px 14px',
                borderRadius: '8px',
                fontSize: '13px',
                fontWeight: '500',
                backgroundColor: notification.type === 'success' ? '#ecfdf5' : '#fef2f2',
                color: notification.type === 'success' ? '#065f46' : '#991b1b',
                border: `1px solid ${notification.type === 'success' ? '#a7f3d0' : '#fecaca'}`,
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}
            >
              <span>{notification.type === 'success' ? '✓' : '⚠️'}</span>
              <span>{notification.text}</span>
            </div>
          )}

          {/* Large Photo Preview Card */}
          <div
            style={{
              position: 'relative',
              width: '180px',
              height: '180px',
              borderRadius: '50%',
              overflow: 'hidden',
              boxShadow: '0 10px 25px -5px rgba(37, 99, 235, 0.25)',
              border: '4px solid #3b82f6',
              background: 'linear-gradient(135deg, #1e40af 0%, #3b82f6 100%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            {photoUrl ? (
              <img
                src={getFullAssetUrl(photoUrl)}
                alt={teacher.teacherName}
                style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                onError={(e) => {
                  e.currentTarget.style.display = 'none';
                  if (e.currentTarget.nextSibling) e.currentTarget.nextSibling.style.display = 'flex';
                }}
              />
            ) : null}
            <div
              style={{
                display: photoUrl ? 'none' : 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#ffffff'
              }}
            >
              <span style={{ fontSize: '42px', fontWeight: '800' }}>{getInitials(teacher.teacherName)}</span>
              <span style={{ fontSize: '11px', opacity: 0.9, marginTop: '4px' }}>No Photo</span>
            </div>

            {isUploadingPhoto && (
              <div
                style={{
                  position: 'absolute',
                  inset: 0,
                  backgroundColor: 'rgba(15, 23, 42, 0.6)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: '#ffffff',
                  fontSize: '13px',
                  fontWeight: '600'
                }}
              >
                Uploading...
              </div>
            )}
          </div>

          {/* Teacher Details */}
          <div style={{ textAlign: 'center' }}>
            <h4 style={{ margin: '0 0 4px 0', fontSize: '18px', fontWeight: '700', color: '#0f172a' }}>
              {teacher.teacherName}
            </h4>
            <div style={{ display: 'flex', gap: '8px', justifyContent: 'center', alignItems: 'center', flexWrap: 'wrap' }}>
              <span
                style={{
                  padding: '2px 8px',
                  borderRadius: '6px',
                  backgroundColor: '#eff6ff',
                  color: '#1e40af',
                  fontSize: '12px',
                  fontWeight: '600',
                  fontFamily: 'monospace'
                }}
              >
                ID: {teacher.teacherId}
              </span>
              <span
                style={{
                  padding: '2px 8px',
                  borderRadius: '6px',
                  backgroundColor: '#f1f5f9',
                  color: '#475569',
                  fontSize: '12px',
                  fontWeight: '600'
                }}
              >
                {teacher.designation || 'Teacher'}
              </span>
              {teacher.teachingWing && (
                <span
                  style={{
                    padding: '2px 8px',
                    borderRadius: '6px',
                    backgroundColor: '#f0fdf4',
                    color: '#166534',
                    fontSize: '12px',
                    fontWeight: '600'
                  }}
                >
                  {teacher.teachingWing}
                </span>
              )}
            </div>
            {(teacher.phoneNumber || teacher.gmail) && (
              <div style={{ marginTop: '6px', fontSize: '12px', color: '#64748b' }}>
                {teacher.phoneNumber} {teacher.phoneNumber && teacher.gmail ? '•' : ''} {teacher.gmail}
              </div>
            )}
          </div>

          {/* Admin Photo Actions */}
          <div style={{ display: 'flex', gap: '10px', width: '100%', justifyContent: 'center' }}>
            <input
              type="file"
              ref={fileInputRef}
              style={{ display: 'none' }}
              accept="image/jpeg,image/png,image/webp"
              onChange={handleFileChange}
            />
            <button
              type="button"
              disabled={isUploadingPhoto}
              onClick={() => fileInputRef.current?.click()}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '6px',
                padding: '8px 16px',
                borderRadius: '8px',
                border: '1px solid #2563eb',
                backgroundColor: '#2563eb',
                color: '#ffffff',
                fontSize: '13px',
                fontWeight: '600',
                cursor: 'pointer',
                transition: 'all 0.15s ease'
              }}
            >
              📷 {photoUrl ? 'Replace Photo' : 'Upload Photo'}
            </button>

            {photoUrl && (
              <button
                type="button"
                disabled={isUploadingPhoto}
                onClick={handleRemovePhoto}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '6px',
                  padding: '8px 16px',
                  borderRadius: '8px',
                  border: '1px solid #fee2e2',
                  backgroundColor: '#fff1f2',
                  color: '#e11d48',
                  fontSize: '13px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  transition: 'all 0.15s ease'
                }}
              >
                🗑️ Remove
              </button>
            )}
          </div>

          {/* Mobile App Permission Section */}
          <div
            style={{
              width: '100%',
              backgroundColor: canUpdatePhoto ? '#f0fdf4' : '#f8fafc',
              border: `1.5px solid ${canUpdatePhoto ? '#86efac' : '#e2e8f0'}`,
              borderRadius: '14px',
              padding: '16px',
              display: 'flex',
              flexDirection: 'column',
              gap: '12px',
              transition: 'all 0.2s ease'
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <div
                  style={{
                    width: '36px',
                    height: '36px',
                    borderRadius: '10px',
                    backgroundColor: canUpdatePhoto ? '#dcfce7' : '#e2e8f0',
                    color: canUpdatePhoto ? '#15803d' : '#64748b',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '18px'
                  }}
                >
                  📱
                </div>
                <div>
                  <div style={{ fontSize: '14px', fontWeight: '700', color: '#0f172a' }}>
                    Mobile App Profile Photo Update
                  </div>
                  <div style={{ fontSize: '12px', color: '#64748b' }}>
                    Permission for teacher to update photo in mobile app
                  </div>
                </div>
              </div>

              {/* iOS-Style Switch */}
              <button
                type="button"
                role="switch"
                aria-checked={canUpdatePhoto}
                disabled={isUpdatingPermission}
                onClick={handleTogglePermission}
                style={{
                  position: 'relative',
                  width: '52px',
                  height: '28px',
                  borderRadius: '14px',
                  backgroundColor: canUpdatePhoto ? '#22c55e' : '#cbd5e1',
                  border: 'none',
                  cursor: isUpdatingPermission ? 'wait' : 'pointer',
                  transition: 'background-color 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                  padding: '2px',
                  outline: 'none',
                  opacity: isUpdatingPermission ? 0.6 : 1
                }}
              >
                <div
                  style={{
                    width: '24px',
                    height: '24px',
                    borderRadius: '12px',
                    backgroundColor: '#ffffff',
                    boxShadow: '0 2px 4px rgba(0, 0, 0, 0.2)',
                    transform: canUpdatePhoto ? 'translateX(24px)' : 'translateX(0)',
                    transition: 'transform 0.2s cubic-bezier(0.4, 0, 0.2, 1)'
                  }}
                />
              </button>
            </div>

            <div
              style={{
                fontSize: '12px',
                lineHeight: '1.4',
                color: canUpdatePhoto ? '#166534' : '#64748b',
                backgroundColor: canUpdatePhoto ? '#dcfce7' : '#ffffff',
                padding: '8px 12px',
                borderRadius: '8px',
                border: `1px solid ${canUpdatePhoto ? '#bbf7d0' : '#e2e8f0'}`
              }}
            >
              {canUpdatePhoto ? (
                <span>
                  ✅ <strong>Allowed:</strong> Teacher is authorized to take a photo or select an image from their mobile gallery to update their profile photo in the mobile app.
                </span>
              ) : (
                <span>
                  🔒 <strong>Locked:</strong> Profile photo cannot be changed via the mobile app. The teacher will see a locked indicator and instructions to contact school administration.
                </span>
              )}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div
          style={{
            padding: '14px 24px',
            borderTop: '1px solid #e2e8f0',
            backgroundColor: '#f8fafc',
            display: 'flex',
            justifyContent: 'flex-end'
          }}
        >
          <button
            type="button"
            onClick={onClose}
            style={{
              padding: '8px 20px',
              borderRadius: '8px',
              border: '1px solid #cbd5e1',
              backgroundColor: '#ffffff',
              color: '#334155',
              fontSize: '13px',
              fontWeight: '600',
              cursor: 'pointer'
            }}
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default TeacherPhotoModal;
